import { memo as memoize } from 'radash'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'

import type { GazelleRef } from '@apsys/gazelle'
import { GazelleRefSchema } from '@apsys/gazelle'

import { LOCAL_STORAGE } from '../constants'
import { EileenColorScheme } from '../schemas/eileen-service-stylesheet'
import type { Manifest } from '../schemas/vixen-core-manifest'
import { debugError, debugLog } from '../utils/debug'

enum Storage {
  language = 'LANGUAGE',
  experience = 'EXPERIENCE',
  version = 'BUNDLE_VERSION',
  themeVariant = 'THEME_VARIANT',
  deviceID = 'DEVICE_ID',
  eventManifest = 'EVENT_MANIFEST',
  esConfig = 'ES_CONFIG',
}

export const setBundleVersion = (version: string): void =>
  localStorage.setItem(Storage.version, version)

export const getBundleVersion = (): string | null =>
  localStorage.getItem(Storage.version)

/** Validate the deviceId value */
const DEVICE_ID_SCHEMA = z.string().uuid()

/**
 * Get the current device ID. Used also for Rudderstack. If we don't have one,
 * we should set one.
 *
 * @internal use {@link getEventMetadata}
 */
export const getDeviceID = memoize((): string => {
  // If the current value is valid, return the current value
  const result = DEVICE_ID_SCHEMA.safeParse(
    localStorage.getItem(LOCAL_STORAGE.DEVICE_ID),
  )

  if (result.success) {
    debugLog('Device Id', 'Existing device', result.data)
    return result.data
  }

  // Else create a new value
  const value = uuidv4()

  debugLog('Device Id', 'Setting new deviceId', value)
  localStorage.setItem(LOCAL_STORAGE.DEVICE_ID, value)

  return value
})

export const setEventManifest = (
  manifest: GazelleRef<Manifest> | undefined,
): void => {
  if (manifest) {
    localStorage.setItem(Storage.eventManifest, JSON.stringify(manifest))
  }
}

/**
 * Returns a GazelleRef to the current manifest from localstorage.
 */
export const getEventManifest = (): GazelleRef<Manifest> | null => {
  const manifest = localStorage.getItem(Storage.eventManifest)

  if (!manifest) {
    return null
  }

  try {
    const manifestObject = JSON.parse(manifest)

    return GazelleRefSchema.parse(manifestObject)
  } catch (e) {
    debugError('Error parsing manifest', e)
  }

  return null
}

/**
 * Set the theme variant in local storage. Do this via {@link AppContext}.
 */
export const setThemeVariant = (
  theme: EileenColorScheme.Mode | null,
): void => {
  if (theme !== null) {
    localStorage.setItem(Storage.themeVariant, theme)
  } else {
    localStorage.removeItem(Storage.themeVariant)
  }
}

const THEME_VARIANT_SCHEMA = z.nativeEnum(EileenColorScheme.Mode).nullable()

/**
 * Retrieve the theme variant from localstorage. However get this value from
 * {@link AppContext}.
 */
export const getThemeVariant = (): EileenColorScheme.Mode | null => {
  const value = THEME_VARIANT_SCHEMA.safeParse(
    localStorage.getItem(Storage.themeVariant),
  )
  if (value.success) {
    return value.data
  }
  return null
}
